import regeneratorRuntime from "regenerator-runtime";
import { FrontApp } from "./front/FrontApp.js";
import { VehiclesListApp } from "./VLP/VehiclesListApp.js";
import { AdminApp } from "./admin/AdminApp.js";
import { PmDRSApi } from "./API/PmDRSApi.js";
import { PmApiDtm } from "./API/PmApiDtm.js";
import { NPAApi } from "./API/NPAApi.js";
import { PmSalesTaxApi } from "./API/PmSalesTaxApi.js";
import { PmDealersite } from "./API/PmDealersite.js";
import { DataDriver } from "./Drivers/DataDriver.js";
import { OfferlogixDriver } from "./Drivers/OfferlogixDriver.js";
import { PixelmotionDriver } from "./Drivers/PixelmotionDriver.js";
import { LenderDeskDriver } from "./Drivers/LenderDeskDriver.js";
import { PmRatesDriver } from "./Drivers/PmRatesDriver.js";
import { BalloonApp } from "./VLP/BalloonApp";
import { DOMHelper } from "./Helpers/DOMHelper.js";
import { PmVLPDatalayer } from "./Datalayer/PmVLPDatalayer.js";
import { PmVDPDatalayer } from "./Datalayer/PmVDPDatalayer.js";
import { PmCustomerAPI } from "./API/PmCustomerAPI";

export default class PMDRS {
    constructor(config) {
        let pmdrs = this;
        this.allDealTypes = ['finance','lease','balloon', 'cash'];//'premier','ttf' are now balloonTypes, no dealtypes
        // this.allDealTypesWidgetV2 = ['finance','lease','balloon', 'cash', 'finance_lower_rate'];
        this.allowedCustomerCashDealTypes = ["cash", "balloon"];

        //CONFIG
        this.config = this.defaultConfig();
        Object.assign(this.config, config);

        // Order-independent of driver/oem — every one of these only ever needs
        // production/dev/accountId, confirmed by reading each constructor. Hoisted ahead of
        // the driver/oem setup below so they're available during vin-resolution too (see
        // _resolveFromVin, which uses this.pmApi before driver/oem are known).
        this.pmApi = new PmDRSApi({
            production: this.config.production,
            siteToken: this.config.accountId,
        });
        this.pmApiDtm = new PmApiDtm({
            production: this.config.production,
            dev: this.config.dev,
        });
        this.pmSalesApi = new PmSalesTaxApi({
            production: this.config.production,
        });
        this.pmDealersite = new PmDealersite({
            production: this.config.production,
        });
        this.NPAApi = new NPAApi({
            production: this.config.production,
        });
        this.pmCustomerAPI = new PmCustomerAPI({
            production: this.config.production,
            dev: this.config.dev,
            siteToken: this.config.accountId
        });

        // Non-PM VIN-only bootstrap: driver/oem aren't known yet (oem is derived from the
        // vehicle's make server-side) — resolve them, plus dealer info and the vehicle
        // itself, from one call before finishing setup. Every existing (PM-site) caller
        // always supplies driver+oem, so this branch is never taken for them — see
        // _completeInit()'s comment for the zero-behavior-change argument.
        if (this.config.vin && !config.driver) {
            this._pendingInit = true;
            this._resolveFromVin(this.config.vin, config);
        } else {
            this._completeInit();
        }
    }

    // Was the tail of the constructor (oem lowercase + setDataDriver + init) — extracted so
    // it can run either immediately (driver/oem supplied) or after _resolveFromVin resolves
    // them (vin-only). For every existing caller this still runs synchronously, in the
    // constructor, at construction time — nothing about its own behavior changed, only that
    // it's now a named step instead of inline tail code.
    _completeInit() {
        this.config.oem = this.config.oem.toLowerCase();

        //SET DRIVERS
        this.setDataDriver(this.config.driver);

        this.init();

        this._pendingInit = false;
        if (this._pendingFrontAppConfig) {
            const queuedConfig = this._pendingFrontAppConfig;
            this._pendingFrontAppConfig = null;
            this.createFrontApp(queuedConfig);
        }
    }

    // Resolves driver/oem/dealer-info/vehicle from a bare VIN (GET /api/vehicle/{siteToken}/{vin},
    // the same endpoint the non-PM VIN-only bootstrap already used as an external pre-fetch —
    // this collapses that bolt-on into PMDRS itself). Fails silently-stuck on error/not-found
    // (same "never calls init(), no further action" pattern FrontApp's own site-settings-fetch
    // error path already uses) unless the caller supplied config.onVehicleResolveError.
    // callerConfig is the raw constructor argument, NOT this.config — this.config already has
    // defaultConfig() merged in (e.g. dealerZipCode: 92832), so checking it would make the
    // default always win over the resolved dealer value.
    _resolveFromVin(vin, callerConfig) {
        const config = this.config;
        this.pmApi.getVehicleByVin(config.accountId, vin,
            (response) => {
                const data = JSON.parse(response);
                if (!data.found) {
                    if (typeof config.onVehicleResolveError === 'function') config.onVehicleResolveError(vin);
                    else console.error("PMDRS: vehicle not found for vin " + vin);
                    return;
                }
                this.config.driver = data.driver;
                this.config.oem = data.oem;
                // don't clobber an explicit caller-supplied value with the resolved one
                this.config.dealerName = callerConfig.dealerName || data.dealerName || config.dealerName;
                this.config.dealerZipCode = callerConfig.dealerZipCode || data.dealerZipCode || config.dealerZipCode;
                this.config.dealerPhone = callerConfig.dealerPhone || data.dealerPhone || config.dealerPhone;
                this._resolvedVehicle = PMDRS.assembleVehicleFromApiResponse(data);
                this._completeInit();
            },
            () => {
                if (typeof config.onVehicleResolveError === 'function') config.onVehicleResolveError(vin);
                else console.error("PMDRS: vehicle lookup failed for vin " + vin);
            }
        );
    }

    // Maps a VehicleController::getByVin response onto FrontApp.defaultConfig().vehicle's
    // shape — same fields the non-PM VIN-only prototype's assembleVehicle() already mapped,
    // kept here (not just in pmdra-embed.js) so any caller using PMDRS's own vin-resolution
    // gets the same shape without re-implementing this mapping itself.
    static assembleVehicleFromApiResponse(data) {
        return {
            finalPrice: data.finalPrice,
            retailPrice: data.retailPrice,
            dealerDiscounts: 0,
            dealerMarkups: 0,
            condition: data.condition,
            vin: data.vin,
            stockNumber: data.stockNumber,
            statusCode: data.statusCode,
            mileage: data.mileage,
            year: data.year,
            make: data.make,
            model: data.model,
            trim: data.trim,
            vehicleKey: "",
            label: data.label,
            vdpUrl: data.vdpUrl,
            imgUrl: data.imgUrl,
            pricing: data.pricing || {},
            accessories: data.accessories || {},
        };
    }

    init() {
        this.addHtml();
    }

    createFrontApp(frontAppConfig) {
        if (this._pendingInit) {
            // vin-resolution hasn't completed yet — queue this call, _completeInit() will
            // re-invoke it for real once driver/oem/vehicle are known. Caller doesn't get a
            // FrontApp instance synchronously in this case (matches the async nature of the
            // vin-only path — same reason the old bolt-on pre-fetch never returned one either).
            this._pendingFrontAppConfig = frontAppConfig;
            return null;
        }
        if (!frontAppConfig.vehicle && this._resolvedVehicle) {
            frontAppConfig = Object.assign({ vehicle: this._resolvedVehicle }, frontAppConfig);
        }
        this.frontApp = new FrontApp(this, frontAppConfig);
        return this.frontApp;
    }

    createAdminApp(adminAppConfig) {
        this.adminApp = new AdminApp(this, adminAppConfig);
    }

    createBalloonApp(balloonConfig) {
        this.balloonApp = new BalloonApp(this, balloonConfig);
    }

    createVehiclesListApp(config) {
        this.vehiclesListApp = new VehiclesListApp(this, config);
        return this.vehiclesListApp;
    }

    addHtml() {
        if(PMDRS.stylesAdded) return;//skip to not double import
        //ICONS
        // document.querySelector('body').insertAdjacentHTML('beforeend',`<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css" integrity="sha512-iBBXm8fW90+nuLcSKlbmrPcLa0OT92xO1BIsZ+ywDWZCvqsWgccV3gFoRBv0z+8dLJgyAHIhR35VZc2oM/gI1w==" crossorigin="anonymous" />`)
        //PMDRS Style
        let cssUrl = "dist/css/" + this.config.cssFile;
        // cssUrl = this.pmApi.getRootURL() + cssUrl;

        if (this.config.dev) {
            cssUrl = "../dist/css/" + this.config.cssFile + "?ver=" + new Date().getTime();
        } else {
            cssUrl = this.pmApi.getRootURL() + cssUrl + "?ver=" + new Date().getTime();
        }        
        // console.log(cssUrl);
        DOMHelper.addExternalStyleSheet(cssUrl);
        DOMHelper.addExternalFonts();
        PMDRS.stylesAdded = true;
    }

    defaultConfig() {
        return {
            oem: "bmwmotorrad",
            dealerId: "888555",
            websiteProviderId: "pixelmotion",
            accountId: "bmwdrs",
            adminLoggedIn: false,
            // accountId: "guptonmotors",
            offerLogixPaymentsAccoundId: "60156", //'47472',
            offerLogixSource: "15903", //Victor CDJR = 15903
            production: false, // FLAG TO POINT API TO pm-drs-api.loc or dra.dev.pixelmotiondemo.com
            dev: false, //FLAG TO USE DEV FILES INSTEAD THE DIST FOLDER ON PM-DRS-API
            dealerName: "",//to display in disclaimers
            dealerPhone: "",//to display in disclaimers
            dealerZipCode: 92832,//default 92832 fullerton 
            driver: "pixelmotion", // offerlogix | lenderdesk
            cssFile: "pmdrs.css",
            isMultiDealer: false,       
            sessionId: null,//for datalayer, tracking customer path. It could be a value or function
            hostSessionVars: {},//Object. It's exclusive to be stored in leads by the moment. Don't build logic using any attribute inside of it
            // callbacks
            onCustomerComplete: function () {//created for things like reseting sessionid handled by host
                console.log("PMDRS: Customer Complete");
            }
        };
    }

    getSessionId() {//this should be the only way to read sessionId
        if (this._fallbackSessionId) {
            // already fell back once — the host callback is known-bad for this pageview,
            // don't keep re-invoking (and re-throwing from) it on every call
            return this._fallbackSessionId;
        }

        let id;
        if (typeof this.config.sessionId === 'function') {
            // host-provided callback (e.g. reads a tracking global that may not have loaded) —
            // never let it take down widget init; fall back to a locally-generated session id
            try {
                id = this.config.sessionId();
            } catch (e) {
                console.error("PMDRS: config.sessionId() threw", e);
            }
        } else {
            id = this.config.sessionId;
        }

        if (id) {
            this.sessionIdSource = "host";
            return id;
        }

        this.sessionIdSource = "fallback";
        console.warn(
            "PMDRS: no usable host session id (tracking script likely blocked or missing) — " +
            "using an internally-generated session id for this session. Records saved to " +
            "customer/lead/journey endpoints will be tagged sessionIdSource='fallback'."
        );
        return this.getFallbackSessionId();
    }

    // true once getSessionId() has fallen back to a locally-generated id for this pageview
    isUsingFallbackSessionId() {
        return this.sessionIdSource === "fallback";
    }

    getFallbackSessionId() {
        if (!this._fallbackSessionId) {
            this._fallbackSessionId = "pmdrs-" + Date.now() + "-" + Math.floor(Math.random() * 1e6);
        }
        return this._fallbackSessionId;
    }

    setDataDriver(driverKey) {
        switch (driverKey) {
            case DataDriver.OFFERLOGIX_KEY:
                this.dataDriver = new OfferlogixDriver(this);
                break;
            case DataDriver.PIXELMOTION_KEY:
                this.dataDriver = new PixelmotionDriver(this);
                break;
            case DataDriver.LENDERDESK_KEY:
                this.dataDriver = new LenderDeskDriver(this);
                break;
            case DataDriver.PMRATES_KEY:
                this.dataDriver = new PmRatesDriver(this);
                break;
        }
    }

    getPmSiteSettings(data, successCallback) {
        this.pmApi.pmSiteSettings.getPmSettings(this.config.accountId, data, successCallback);
    }

    getOEM() {
        return this.config.oem;
    }

    static testingCustomerAPIArray(site_token) {
        return ['bmwdrs', 'devmotosamerica', 'victorcdjrdemo', 'motojulia', 'bmwmcriverside', 'newcenturymoto'].includes(site_token);
    }

    isATestSite(site_token) {
        return ['bmwdrs', 'devmotosamerica', 'victorcdjrdemo', 'motojulia', 'bmwmcriverside', 'newcenturymoto'].includes(site_token);
    }

    isMotosAmericaSite()
    {
        let motosAmericaSTs = [
            'devmotosamerica', 'motosamerica', 'eurocyclesoftampabay', 'eurocyclesoforlando', 'eurocyclesofdaytona', 'cascademoto',
            'cascademotoeugene', 'northwestmoto', 'triumphofseattle', 'triumphofsantamonica', 'triumphcolumbiariver', 'marinspeedshop', 'motojulia',
            'nashvillemotos'
        ];
        return motosAmericaSTs.includes(this.config.accountId);
    }

    isSosoSite(){
        return [
            'sosocycles',
            'bmwmcconcord',
            'bmwmotorcycle',
            'southsoundmotorcycles'
        ].includes(this.config.accountId);
    }

    MAPSiteTokens() {
        return ["bmwmcsat", "rgvcycles"];
    }

    isFCA() {
        let oem = this.config.oem;
        if (oem.toLowerCase() == "fca") return true;
        return false;
    }

    getBalloonPaymentTitle(balloonType){
        switch(balloonType){
            case 'premier': return 'Premier';
            case 'ttf': return 'Triple Financing';
            case 'easyride': return 'Easy Ride';
            case 'easyridecash': return 'Easy Ride Cash';
        }        
        return 'Balloon';
    }

    /**
     * config = {production: boolean, accountId: string}
     * */
    static initSiteTracking(config) {
        var vlpDatalayer = null;
        var vdpDatalayer = null;

        var pmCustomerApi = new PmCustomerAPI({
            production: config.production,
            dev: config.dev,
            siteToken: config.accountId,
        });
        vlpDatalayer = new PmVLPDatalayer(pmCustomerApi);
        vdpDatalayer = new PmVDPDatalayer(pmCustomerApi);

        vlpDatalayer.init();
        vdpDatalayer.init();
    }
}
